import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCsrfToken } from '../../../utils/csrf';

interface StockFormData {
  operationType: string | null;
  currency: string | null;
  country: string | null;
  negotiationMarket: string | null;
  operationDate: string | null;
  ticker: string | null;
  volume: string | null;
  price: string | null;
  confirm?: string | null;
}

interface FormData extends StockFormData {}

export const useInvestmentsStockImport = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    country: null,
    negotiationMarket: null,
    operationType: '',
    currency: null,
    operationDate: new Date().toISOString().split('T')[0],
    ticker: '',
    volume: '',
    price: '',
  });
  const navigate = useNavigate();
  const [countries, setCountries] = useState<string[]>([]);
  const [negotiationMarkets, setNegotiationMarkets] = useState<Record<string, string[]>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const totalSteps = 8;

  const fetchInvestments = useCallback(async () => {
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch('/api/investments/import/', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        return data;
      }
      console.error('Failed to fetch investments');
    } catch (error) {
      console.error('Error:', error);
    }
  }, []);

  useEffect(() => {
    const loadInvestmentOptions = async () => {
      const result = await fetchInvestments();
      if (result?.success) {
        setCountries(result.data.countries);
        setNegotiationMarkets(result.data.negotiation_markets);
      }
    };
    
    loadInvestmentOptions();
  }, [fetchInvestments]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'ticker' && value && e.type === 'change' && !(e.nativeEvent as InputEvent).inputType) {
      setCurrentStep(7);
    }
  };

  const handleSelectOption = async (field: keyof StockFormData, value: string) => {
    if (isSubmitting) return;
    
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'confirm' && value === 'yes') {
      setIsSubmitting(true);
      try {
        const requiredFields: (keyof FormData)[] = [
          'operationType',
          'currency',
          'operationDate',
          'ticker',
          'volume',
          'price',
          'country', 
          'negotiationMarket'
        ];

        const emptyFields = requiredFields.filter(field => !formData[field]);
        
        if (emptyFields.length > 0) {
          console.error('Form validation failed:', emptyFields);
          return;
        }

        const csrfToken = await getCsrfToken();
        const formPayload = {
          operation_type: formData.operationType || '',
          currency: formData.currency || '',
          country: formData.country || '',
          negotiation_market: formData.negotiationMarket || '',
          operation_date: formData.operationDate || '',
          ticker: formData.ticker || '',
          volume: formData.volume || '0',
          price: formData.price || '0',
          invested_amount: (parseFloat(formData.volume || '0') * parseFloat(formData.price || '0')).toString()
        };

        const response = await fetch('/api/investments/import/stocks/', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
          body: JSON.stringify(formPayload),
        });

        if (response.ok) {
          window.location.href = '/investments';
        } else {
          const errorData = await response.json();
          console.error('API submission failed:', errorData);
        }
      } catch (error) {
        console.error('Submission error:', error);
      } finally {
        setIsSubmitting(false);
      }
    } else if (field === 'confirm' && value === 'no') {
      setCurrentStep(7);
    }
  };

  const navigateToStep = (step: number) => {
    // Validate current step before moving forward
    if (step > currentStep) {
      let canProceed = true;
      
      switch (currentStep) {
        case 1:
          canProceed = !!formData.country;
          break;
        case 2:
          canProceed = !!formData.negotiationMarket;
          break;
        case 3:
          canProceed = !!formData.operationType;
          break;
        case 4:
          canProceed = !!formData.currency;
          break;
        case 5:
          canProceed = !!formData.operationDate;
          break;
        case 6:
          canProceed = !!formData.ticker;
          break;
        case 7:
          canProceed = !!formData.volume && !!formData.price;
          break;
      }

      if (!canProceed) {
        console.warn('Please fill in the current field before proceeding');
        return;
      }

      // Simply set to next step without skipping
      setCurrentStep(step);
      return;
    }
    
    setCurrentStep(step);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Only validate on final submission
    if (currentStep === 7) {
      const requiredFields: (keyof FormData)[] = [
        'operationType',
        'currency',
        'operationDate',
        'ticker',
        'volume',
        'price',
        'country', 
        'negotiationMarket'
      ];

      const emptyFields = requiredFields.filter(field => !formData[field]);
      
      if (emptyFields.length > 0) {
        console.error('Missing required fields:', emptyFields);
        return;
      }
      
      try {
        const csrfToken = await getCsrfToken();
        
        const formPayload = {
          operation_type: formData.operationType || '',
          currency: formData.currency || '',
          country: formData.country || '',
          negotiation_market: formData.negotiationMarket || '',
          operation_date: formData.operationDate || '',
          ticker: formData.ticker || '',
          volume: formData.volume || '0',
          price: formData.price || '0',
          invested_amount: (parseFloat(formData.volume || '0') * parseFloat(formData.price || '0')).toString()
        };

        const response = await fetch('/api/investments/import/stocks/', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
          body: JSON.stringify(formPayload),
        });

        if (response.ok) {
          navigate('/investments');
        } else {
          const errorData = await response.json();
          console.error('Form submission failed:', errorData.error);
        }
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  return {
    currentStep,
    formData,
    countries,
    negotiationMarkets,
    totalSteps,
    handleInputChange,
    handleSelectOption,
    navigateToStep,
    handleSubmit,
    isSubmitting,
  };
};
